/**
 * VehicleEconomyTab - Ekonomi-flik i fordonskortet
 * Leasing (inkl. skatt/malus), Försäkring, Avveckling + kostnadsställe per kostnadsslag.
 *
 * Komboboxar – datakälla och lagring:
 * - Leasingbolag / Försäkringsbolag / Bilförsäljare: Hämtas från API getParties (tabell vehicle_economy_parties).
 *   Sparas i vehicle_economy som leasing_company_id, insurance_company_id, vehicle_supplier_id.
 * - Kostnadsställe (alla): Hämtas från API getCostCenters (tabell cost_centers). Sparas i vehicle_economy
 *   som cost_center_id_leasing, cost_center_id_insurance, cost_center_id_tax, cost_center_id_tax_malus.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/store/authStore';
import {
  getEconomy,
  getEconomyHistory,
  saveEconomy,
  getParties,
  getCostCenters,
  type VehicleEconomyHistoryRow,
} from '@/services/vehicleEconomy/vehicleEconomyService';
import type {
  VehicleEconomy,
  VehicleEconomyParty,
  CostCenterOption,
} from '@/types/vehicleEconomy';
import { emptyEconomyForm, formFromEconomy, type EconomyFormState } from './economyForm';
import {
  getVehicleInsuranceHistory,
  type VehicleInsuranceHistoryRow,
} from '@/services/deque/protectorInsuranceService';
import {
  listVehicleCostLines,
  type VehicleCostLine,
} from '@/services/vehicleEconomy/costInvoiceApi';

interface VehicleEconomyTabProps {
  vehicleId: number;
  organizationId: string;
}

const VehicleEconomyTab: React.FC<VehicleEconomyTabProps> = ({ vehicleId, organizationId }) => {
  const { t } = useTranslation();
  const { hasFeatureAccess } = usePermissions();
  const isGlobalAdmin = useAuthStore((s) => s.user?.is_global_admin === true);
  const canEditEconomy =
    isGlobalAdmin || hasFeatureAccess('SYSTEM_ECONOMY') || hasFeatureAccess('FLEET');
  const [, setEconomy] = useState<VehicleEconomy | null>(null);
  const [parties, setParties] = useState<VehicleEconomyParty[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<EconomyFormState>(emptyEconomyForm);
  const [history, setHistory] = useState<VehicleEconomyHistoryRow[]>([]);
  const [insuranceHistory, setInsuranceHistory] = useState<VehicleInsuranceHistoryRow[]>([]);
  const [costLines, setCostLines] = useState<VehicleCostLine[]>([]);
  const costSummary = useMemo(() => {
    const byCategory = new Map<string, { name: string; count: number; amount: number }>();
    let total = 0;
    let from: string | null = null;
    let to: string | null = null;
    for (const line of costLines) {
      const key = line.categoryCode || line.categoryName;
      const cur = byCategory.get(key) ?? { name: line.categoryName, count: 0, amount: 0 };
      cur.count += 1;
      cur.amount += line.amountExclVat;
      byCategory.set(key, cur);
      total += line.amountExclVat;
      if (line.invoiceDate) {
        if (!from || line.invoiceDate < from) from = line.invoiceDate;
        if (!to || line.invoiceDate > to) to = line.invoiceDate;
      }
    }
    return {
      rows: [...byCategory.values()].sort((a, b) => b.amount - a.amount),
      total,
      count: costLines.length,
      from,
      to,
    };
  }, [costLines]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [e, p, cc, hist, insHist, lines] = await Promise.all([
        getEconomy(vehicleId),
        getParties(organizationId),
        getCostCenters(organizationId),
        getEconomyHistory(vehicleId).catch(() => []),
        getVehicleInsuranceHistory(vehicleId).catch(() => []),
        listVehicleCostLines(vehicleId).catch(() => []),
      ]);
      setHistory(hist);
      setInsuranceHistory(insHist);
      setCostLines(lines);
      setEconomy(e);
      setParties(p);
      setCostCenters(cc);
      setForm(e ? formFromEconomy(e) : emptyEconomyForm);
    } catch (err: any) {
      setError(err?.message || 'Kunde inte ladda ekonomi');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, organizationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateForm = (updates: Partial<EconomyFormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await saveEconomy(vehicleId, form);
      setEconomy(saved);
      setForm(formFromEconomy(saved));
      setSuccess(t('vehicleEconomy.saveSuccess'));
    } catch (err: any) {
      setError(err?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const leasingParties = parties.filter((p) => p.type === 'LEASING');
  const insuranceParties = parties.filter((p) => p.type === 'INSURANCE');
  const supplierParties = parties.filter((p) => p.type === 'VEHICLE_SUPPLIER');

  const select = (value: string | null | undefined, options: { id: string; name: string }[], label: string) => (
    <select
      className="mt-1 block w-full rounded border border-gray-300 py-2 text-base disabled:bg-gray-100"
      value={value ?? ''}
      disabled={!canEditEconomy}
      onChange={(e) => updateForm({ [label]: e.target.value || null })}
    >
      <option value="">—</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );

  const costCenterSelect = (value: string | null | undefined, field: keyof EconomyFormState) => (
    <select
      className="mt-1 block w-full rounded border border-gray-300 py-2 text-base disabled:bg-gray-100"
      value={value ?? ''}
      disabled={!canEditEconomy}
      onChange={(e) => updateForm({ [field]: e.target.value || null })}
    >
      <option value="">—</option>
      {costCenters.map((c) => (
        <option key={c.id} value={c.id}>
          {c.code} – {c.description}
        </option>
      ))}
    </select>
  );

  const input = (
    type: 'text' | 'number' | 'date',
    value: string | number | null | undefined,
    field: keyof EconomyFormState,
    placeholder?: string
  ) => (
    <input
      type={type}
      className="mt-1 block w-full rounded border border-gray-300 py-2 text-base disabled:bg-gray-100"
      value={value ?? ''}
      disabled={!canEditEconomy}
      onChange={(e) => {
        const v = e.target.value;
        if (type === 'number') updateForm({ [field]: v === '' ? null : Number(v) });
        else updateForm({ [field]: v || null });
      }}
      placeholder={placeholder}
    />
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <p className="text-base text-textTertiary">{t('common.loading') || 'Laddar...'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 text-base">
      {error && (
        <div className="rounded bg-red-50 p-3 text-base text-red-800">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded bg-green-50 p-3 text-base text-green-800">
          {success}
        </div>
      )}

      {/* Leasing */}
      <section className="rounded-lg border bg-gray-50 p-4">
        <h3 className="mb-3 text-base font-semibold text-gray-800">
          {t('vehicleEconomy.leasing') || 'Leasing'}
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.ownershipType')}</label>
            <select
              className="mt-1 block w-full rounded border border-gray-300 py-2 text-base"
              value={form.ownership_type ?? ''}
              disabled={!canEditEconomy}
              onChange={(e) =>
                updateForm({
                  ownership_type: e.target.value || null,
                  ownership_source: 'manual',
                })
              }
            >
              <option value="">—</option>
              <option value="owned">{t('fleet.ownershipOwned')}</option>
              <option value="leased">{t('fleet.ownershipLeased')}</option>
              <option value="lease_ended">{t('fleet.ownershipLeaseEnded')}</option>
            </select>
            <p className="mt-1 text-xs text-textTertiary">{t('vehicleEconomy.ownershipHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.agreementNo') || 'Avtalsnr'}</label>
            {input('text', form.agreement_no, 'agreement_no')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.contractNo') || 'Kontraktsnr'}</label>
            {input('text', form.contract_no, 'contract_no')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.leasingCompany') || 'Leasingbolag'}</label>
            {select(form.leasing_company_id, leasingParties, 'leasing_company_id')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.rentStart') || 'Hyresstart'}</label>
            {input('date', form.rent_start, 'rent_start')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.rentEnd') || 'Hyresslut'}</label>
            {input('date', form.rent_end, 'rent_end')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.termMonths') || 'Avtalstid (mån)'}</label>
            {input('number', form.term_months, 'term_months')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.costMonthLeasing') || 'Månadskostnad leasing'}</label>
            {input('number', form.cost_month_leasing, 'cost_month_leasing')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.leasingProduct') || 'Produkt'}</label>
            {input('text', form.leasing_product, 'leasing_product')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.originalBasePrice') || 'Ursp baspris'}</label>
            {input('number', form.original_base_price, 'original_base_price')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.bookValue') || 'Bokfört värde'}</label>
            {input('number', form.book_value, 'book_value')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.guaranteedResidualValue') || 'Garanterat restvärde'}</label>
            {input('number', form.guaranteed_residual_value, 'guaranteed_residual_value')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.allowedDistance') || 'Tillåten km'}</label>
            {input('number', form.allowed_distance, 'allowed_distance')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.costPerExtraKm') || 'Kostnad extra km'}</label>
            {input('number', form.cost_per_extra_km, 'cost_per_extra_km')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('vehicleEconomy.costCenterLeasing') || 'Kostnadsställe (leasing)'}
            </label>
            {costCenterSelect(form.cost_center_id_leasing, 'cost_center_id_leasing')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.costMonthTax') || 'Månadskostnad skatt'}</label>
            {input('number', form.cost_month_tax, 'cost_month_tax')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.costMonthTaxMalus') || 'Månadskostnad skatt malus'}</label>
            {input('number', form.cost_month_tax_malus, 'cost_month_tax_malus')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('vehicleEconomy.costCenterTax') || 'Kostnadsställe (skatt)'}
            </label>
            {costCenterSelect(form.cost_center_id_tax, 'cost_center_id_tax')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('vehicleEconomy.costCenterTaxMalus') || 'Kostnadsställe (malus)'}
            </label>
            {costCenterSelect(form.cost_center_id_tax_malus, 'cost_center_id_tax_malus')}
          </div>
        </div>
      </section>

      {/* Försäkring */}
      <section className="rounded-lg border bg-gray-50 p-4">
        <h3 className="mb-3 text-base font-semibold text-gray-800">
          {t('vehicleEconomy.insurance') || 'Försäkring'}
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.insuranceCompany') || 'Försäkringsbolag'}</label>
            {select(form.insurance_company_id, insuranceParties, 'insurance_company_id')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.insuranceAgreementNo') || 'Avtal'}</label>
            {input('text', form.insurance_agreement_no, 'insurance_agreement_no')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.insuranceStartDate') || 'Startdatum'}</label>
            {input('date', form.insurance_start_date, 'insurance_start_date')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.insuranceEndDate') || 'Slutdatum'}</label>
            {input('date', form.insurance_end_date, 'insurance_end_date')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.costMonthInsurance') || 'Månadskostnad försäkring'}</label>
            {input('number', form.cost_month_insurance, 'cost_month_insurance')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('vehicleEconomy.costCenterInsurance') || 'Kostnadsställe (försäkring)'}
            </label>
            {costCenterSelect(form.cost_center_id_insurance, 'cost_center_id_insurance')}
          </div>
        </div>
      </section>

      {/* Avveckling */}
      <section className="rounded-lg border bg-gray-50 p-4">
        <h3 className="mb-3 text-base font-semibold text-gray-800">
          {t('vehicleEconomy.disposal')}
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.disposalType')}</label>
            <select
              className="mt-1 block w-full rounded border border-gray-300 py-2 text-base disabled:bg-gray-100"
              value={form.disposal_type ?? ''}
              disabled={!canEditEconomy}
              onChange={(e) =>
                updateForm({
                  disposal_type: e.target.value === 'sale' || e.target.value === 'return' ? e.target.value : null,
                })
              }
            >
              <option value="">—</option>
              <option value="sale">{t('vehicleEconomy.disposalTypeSale')}</option>
              <option value="return">{t('vehicleEconomy.disposalTypeReturn')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.vehicleSupplier')}</label>
            {select(form.vehicle_supplier_id, supplierParties, 'vehicle_supplier_id')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.disposalDate')}</label>
            {input('date', form.disposal_date, 'disposal_date')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.disposalSaleAmount')}</label>
            {input('number', form.disposal_sale_amount, 'disposal_sale_amount')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.disposalExtraCosts')}</label>
            {input('number', form.disposal_extra_costs, 'disposal_extra_costs')}
          </div>
          <div className="col-span-2 sm:col-span-3">
            <label className="block text-sm font-medium text-gray-700">{t('vehicleEconomy.disposalNotes')}</label>
            <textarea
              className="mt-1 block w-full rounded border border-gray-300 py-2 text-base disabled:bg-gray-100"
              rows={2}
              maxLength={500}
              value={form.disposal_notes ?? ''}
              disabled={!canEditEconomy}
              onChange={(e) => updateForm({ disposal_notes: e.target.value || null })}
            />
          </div>
        </div>
      </section>

      {history.length > 0 && (
        <section className="rounded-lg border bg-gray-50 p-4">
          <h3 className="mb-3 text-base font-semibold text-gray-800">
            {t('vehicleEconomy.importHistory')}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-textSecondary">
                  <th className="py-2 pr-4">{t('vehicleEconomy.historyPeriod')}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.costMonthLeasing')}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.historyInvoice')}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.historyPer')}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.bookValue')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.periodEndDate} className="border-b border-gray-200">
                    <td className="py-2 pr-4">{h.periodEndDate}</td>
                    <td className="py-2 pr-4">{h.paymentAmount ?? h.costMonthLeasing ?? '—'}</td>
                    <td className="py-2 pr-4">{h.invoiceReference ?? '—'}</td>
                    <td className="py-2 pr-4">{h.periodNumber ?? '—'}</td>
                    <td className="py-2 pr-4">{h.bookValue ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {costSummary.count > 0 && (
        <section className="rounded-lg border bg-gray-50 p-4">
          <h3 className="mb-1 text-base font-semibold text-gray-800">
            {t('vehicleEconomy.costInvoiceSummary')}
          </h3>
          <p className="mb-3 text-sm text-textSecondary">
            {t('vehicleEconomy.costInvoiceSummaryMeta', {
              count: costSummary.count,
              period:
                costSummary.from && costSummary.to
                  ? `${costSummary.from} – ${costSummary.to}`
                  : '—',
            })}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-textSecondary">
                  <th className="py-2 pr-4">{t('vehicleEconomy.costInvoiceCategory')}</th>
                  <th className="py-2 pr-4 text-right">{t('vehicleEconomy.costInvoiceSummaryCount')}</th>
                  <th className="py-2 pr-4 text-right">{t('vehicleEconomy.costInvoiceAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {costSummary.rows.map((row) => (
                  <tr key={row.name} className="border-b border-gray-200">
                    <td className="py-2 pr-4">{row.name}</td>
                    <td className="py-2 pr-4 text-right">{row.count}</td>
                    <td className="py-2 pr-4 text-right">
                      {row.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="py-2 pr-4">{t('vehicleEconomy.costInvoiceSummaryTotal')}</td>
                  <td className="py-2 pr-4 text-right">{costSummary.count}</td>
                  <td className="py-2 pr-4 text-right">
                    {costSummary.total.toLocaleString('sv-SE', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {insuranceHistory.length > 0 && (
        <section className="rounded-lg border bg-gray-50 p-4">
          <h3 className="mb-3 text-base font-semibold text-gray-800">
            {t('vehicleEconomy.insuranceImportHistory') || 'Historik (Protector-import)'}
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-textSecondary">
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryPeriod') || 'Period (Til datum)'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryAgreement') || 'Avtal'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryTrafficTotal') || 'Trafik tot'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryDelkasko') || 'Delk'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryVagnskada') || 'Vagn'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryPremiumYear') || 'Belopp'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryTotal') || 'Total'}</th>
                  <th className="py-2 pr-4">{t('vehicleEconomy.insHistoryCostMonth') || 'Månad'}</th>
                </tr>
              </thead>
              <tbody>
                {insuranceHistory.map((h) => (
                  <tr key={h.periodEndDate} className="border-b border-gray-200">
                    <td className="py-2 pr-4">{h.periodEndDate}</td>
                    <td className="py-2 pr-4">{h.agreementNo ?? '—'}</td>
                    <td className="py-2 pr-4">{h.trafficTotal ?? '—'}</td>
                    <td className="py-2 pr-4">{h.delkasko ?? '—'}</td>
                    <td className="py-2 pr-4">{h.vagnskada ?? '—'}</td>
                    <td className="py-2 pr-4">{h.premiumYear ?? '—'}</td>
                    <td className="py-2 pr-4">{h.totalAmount ?? '—'}</td>
                    <td className="py-2 pr-4">{h.costMonth ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canEditEconomy ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded bg-primary px-4 py-2 text-base font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (t('common.saving') || 'Sparar...') : (t('common.save') || 'Spara')}
          </button>
        </div>
      ) : (
        <p className="text-sm text-textSecondary">{t('vehicleEconomy.readOnlyDeque')}</p>
      )}
    </div>
  );
};

export default VehicleEconomyTab;
